import { Controller } from "@hotwired/stimulus"
import * as serverTime from "../lib/server_time"

export default class extends Controller {
  static targets = [
    "textAmount", "dollarAmount", "submitBtn",
    "bbIds", "upIds", "otherDocsIds",
    "bbDropzone", "upDropzone", "otherDropzone",
    "bbFileList", "upFileList", "otherFileList",
    "alternatesContainer", "alternateRow",
    "addendaCheckbox",
    "lineItemRow", "lineItemPrice", "lineItemQty", "lineItemTotal", "grandTotal",
    "lineItemBidsJson",
    "alternateListInput", "alternateListError",
    "withdrawModal",
    "countrySelect", "stateSelect",
    "esignInput",
    "amountWarning", "amountWarningMsg",
    "bidAmountError", "textAmountError", "addendaError", "bidBondError", "unitPricingDocError",
    "otherDocError", "unitPricingListError", "esignError", "unitPricingListInput",
    "requiredField", "deadlineBanner"
  ]

  static values = {
    projectId: Number,
    delimiter: { type: String, default: "," },
    separator: { type: String, default: "." },
    currency: { type: String, default: "$" },
    currencyFormat: { type: String, default: "us_uk_currency_format" },
    bidLeveling: { type: Boolean, default: false },
    uploadUrl: String,
    existingBb: { type: String, default: "[]" },
    existingUp: { type: String, default: "[]" },
    existingOther: { type: String, default: "[]" },
    signerName: { type: String, default: "" },
    validateAmountUrl: { type: String, default: "" },
    deadlineUtc: { type: String, default: "" },
    userId: { type: Number, default: 0 },
    serverNow: { type: Number, default: 0 },
    i18n: { type: Object, default: {} }  // pages.bid_form_js — falls back to the English below
  }

  connect() {
    this._bbIds = new Set()
    this._upIds = new Set()
    this._otherIds = new Set()
    this._amountMismatch = false
    this._deadlineReached = false

    // Load existing attachment IDs
    if (this.hasBbIdsTarget) {
      this.bbIdsTarget.value.trim().split(/\s+/).filter(Boolean).forEach(id => this._bbIds.add(id))
    }
    if (this.hasUpIdsTarget) {
      this.upIdsTarget.value.trim().split(/\s+/).filter(Boolean).forEach(id => this._upIds.add(id))
    }
    if (this.hasOtherDocsIdsTarget) {
      this.otherDocsIdsTarget.value.trim().split(/\s+/).filter(Boolean).forEach(id => this._otherIds.add(id))
    }

    // Calculate initial line item totals if bid leveling
    if (this.bidLevelingValue) {
      this._recalculateAllLineItems()
    }

    // Start deadline monitoring
    if (this.deadlineUtcValue) {
      if (this.serverNowValue) serverTime.seed(this.serverNowValue)
      if (this.userIdValue) serverTime.subscribe(this.userIdValue)
      this._checkDeadline()
      this._deadlineInterval = setInterval(() => this._checkDeadline(), 1000)

      // Re-sync and re-check deadline when user returns to this tab/window
      this._onVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          serverTime.requestSync()
          this._checkDeadline()
        }
      }
      document.addEventListener("visibilitychange", this._onVisibilityChange)
    }
  }

  disconnect() {
    if (this._deadlineInterval) {
      clearInterval(this._deadlineInterval)
      this._deadlineInterval = null
    }
    if (this._onVisibilityChange) document.removeEventListener("visibilitychange", this._onVisibilityChange)
    if (this.deadlineUtcValue && this.userIdValue) serverTime.unsubscribe()
  }

  // ── Currency formatting ─────────────────────────────────────────

  formatCurrency(event) {
    const input = event.currentTarget
    let value = input.value.trim()
    if (!value) return

    // Parse to number
    const num = this._parseCurrencyToNumber(value)
    if (isNaN(num)) return

    // Format back
    input.value = this._formatNumber(num)
  }

  formatWholeNumber(event) {
    const input = event.currentTarget
    let value = input.value.trim()
    if (!value) return

    const num = this._parseCurrencyToNumber(value)
    if (isNaN(num)) return

    input.value = this._formatWholeNumber(Math.round(num))
  }

  _parseCurrencyToNumber(str) {
    const fmt = this.currencyFormatValue
    let normalized
    if (fmt === "european_currency_format") {
      normalized = str.replace(/\./g, "").replace(",", ".")
    } else if (fmt === "swiss_currency_format") {
      normalized = str.replace(/'/g, "")
    } else {
      normalized = str.replace(/,/g, "")
    }
    return parseFloat(normalized)
  }

  _formatNumber(num) {
    const sep = this.separatorValue
    const del = this.delimiterValue
    const parts = num.toFixed(2).split(".")
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, del)
    return parts.join(sep)
  }

  _formatWholeNumber(num) {
    const del = this.delimiterValue
    return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, del)
  }

  // ── Input restrictions ────────────────────────────────────────────

  blockDigits(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault()
    }
  }

  blockLetters(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
      event.preventDefault()
    }
  }

  blockDecimalSeparator(event) {
    if (event.key === this.separatorValue) {
      event.preventDefault()
    }
  }

  // ── File upload ─────────────────────────────────────────────────

  triggerFileUpload(event) {
    const dropzone = event.currentTarget
    const fileInput = dropzone.querySelector("input[type='file']")
    if (fileInput) fileInput.click()
  }

  async uploadFile(event) {
    const input = event.currentTarget
    const files = Array.from(input.files)
    if (!files.length) return

    const docType = input.dataset.docType
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const container = this._fileListForDocType(docType)

    // Show ALL loading chips instantly
    const chips = files.map(file => {
      const chip = this._createLoadingChip(file.name)
      if (container) container.appendChild(chip)
      return chip
    })

    // Upload all files in parallel
    const uploads = files.map((file, i) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("doc_type", docType)

      return fetch(this.uploadUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData
      }).then(resp => {
        if (!resp.ok) throw new Error(this._t("upload_failed", "Upload failed"))
        return resp.json()
      }).then(data => {
        const attachmentId = String(data.id || data.attachment_id)

        if (docType === "BidBond") {
          this._bbIds.add(attachmentId)
          this._syncHiddenField("bb")
        } else if (docType === "UnitPricing") {
          this._upIds.add(attachmentId)
          this._syncHiddenField("up")
        } else {
          this._otherIds.add(attachmentId)
          this._syncHiddenField("other")
        }

        // Replace loading chip with final chip
        chips[i].remove()
        if (container) this._addFileEntry(container, file.name, attachmentId, docType)
        this._clearDocError(docType)
      }).catch(err => {
        console.error("File upload error:", err)
        chips[i].remove()
      })
    })

    await Promise.allSettled(uploads)

    // Reset file input
    input.value = ""
  }

  _fileListForDocType(docType) {
    if (docType === "BidBond" && this.hasBbFileListTarget) return this.bbFileListTarget
    if (docType === "UnitPricing" && this.hasUpFileListTarget) return this.upFileListTarget
    const lists = this.otherFileListTargets
    return lists.find(l => l.dataset.docType === docType) || lists[0] || null
  }

  _createLoadingChip(name) {
    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-md text-xs"
    chip.classList.add("chip-uploading")
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span class="truncate max-w-[180px]">${this._escapeHtml(truncatedName)}</span>
    `
    return chip
  }

  removeAttachment(event) {
    const btn = event.currentTarget
    const id = btn.dataset.attachmentId
    const docType = btn.dataset.docType

    if (docType === "BidBond") {
      this._bbIds.delete(id)
      this._syncHiddenField("bb")
    } else if (docType === "UnitPricing") {
      this._upIds.delete(id)
      this._syncHiddenField("up")
    } else {
      this._otherIds.delete(id)
      this._syncHiddenField("other")
    }

    const chip = btn.parentElement.closest("[data-attachment-id]") || btn.closest("[data-attachment-id]")
    if (chip) chip.remove()
  }

  _syncHiddenField(type) {
    if (type === "bb" && this.hasBbIdsTarget) {
      this.bbIdsTarget.value = [...this._bbIds].join(" ")
    } else if (type === "up" && this.hasUpIdsTarget) {
      this.upIdsTarget.value = [...this._upIds].join(" ")
    } else if (type === "other" && this.hasOtherDocsIdsTarget) {
      this.otherDocsIdsTarget.value = [...this._otherIds].join(" ")
    }
  }

  dragOver(e) {
    e.preventDefault()
    const dropzone = e.currentTarget
    dropzone.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(e) {
    e.preventDefault()
    const dropzone = e.currentTarget
    dropzone.classList.remove("border-brand", "bg-brand/5")
  }

  dropFiles(e) {
    e.preventDefault()
    const dropzone = e.currentTarget
    dropzone.classList.remove("border-brand", "bg-brand/5")

    if (e.dataTransfer.files.length) {
      const docType = dropzone.dataset.docType
      const input = dropzone.querySelector("input[type='file']")
      // Create a synthetic event-like object and reuse uploadFile logic
      const dt = new DataTransfer()
      Array.from(e.dataTransfer.files).forEach(f => dt.items.add(f))
      input.files = dt.files
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  _addFileEntry(container, name, attachmentId, docType) {
    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name
    const chip = document.createElement("div")
    chip.className = "inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md text-xs"
    chip.classList.add("chip-file")
    chip.dataset.attachmentId = attachmentId
    chip.innerHTML = `
      <svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
      </svg>
      <span class="truncate max-w-[180px]" title="${this._escapeHtml(name)}">${this._escapeHtml(truncatedName)}</span>
      <button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-300 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              data-action="click->bid-form#removeAttachment"
              data-doc-type="${docType}"
              data-attachment-id="${attachmentId}">
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    `
    container.appendChild(chip)
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  // ── Alternates ──────────────────────────────────────────────────

  addAlternate() {
    if (!this.hasAlternatesContainerTarget) return

    const count = this.alternatesContainerTarget.querySelectorAll("[data-bid-form-target='alternateRow']").length
    const idx = count + 1

    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    row.dataset.bidFormTarget = "alternateRow"
    row.innerHTML = `
      <span class="text-[13px] text-gray-500 w-6">${idx}.</span>
      <span class="text-[13px] text-gray-500">${this._escapeHtml(this.currencyValue)}</span>
      <input type="text" name="bid[alternate_amounts][]"
             data-action="blur->bid-form#formatCurrency"
             class="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] focus:outline-none focus:border-brand">
      <button type="button"
              class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
              data-action="click->bid-form#removeAlternate">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    `
    this.alternatesContainerTarget.appendChild(row)
  }

  removeAlternate(event) {
    const row = event.currentTarget.closest("[data-bid-form-target='alternateRow']")
    if (row) row.remove()

    // Re-number
    this.alternatesContainerTarget.querySelectorAll("[data-bid-form-target='alternateRow']").forEach((r, i) => {
      const num = r.querySelector("span")
      if (num) num.textContent = `${i + 1}.`
    })
  }

  // ── Line item bids (SOV bid leveling) ───────────────────────────

  calculateLineItemTotal(event) {
    const row = event.currentTarget.closest("tr")
    if (!row) return

    const priceInput = row.querySelector("[data-bid-form-target='lineItemPrice']")
    const qtyInput = row.querySelector("[data-bid-form-target='lineItemQty']")
    const totalCell = row.querySelector("[data-bid-form-target='lineItemTotal']")

    if (!priceInput || !totalCell) return

    const price = this._parseCurrencyToNumber(priceInput.value || "0")
    const qty = parseFloat(qtyInput?.value || "0") || 0
    const total = price * qty

    totalCell.textContent = total > 0 ? this._formatNumber(total) : ""

    this._updateGrandTotal()
    this._updateDollarAmount()
  }

  _recalculateAllLineItems() {
    if (!this.hasLineItemRowTarget) return

    this.lineItemRowTargets.forEach(row => {
      const priceInput = row.querySelector("[data-bid-form-target='lineItemPrice']")
      const qtyInput = row.querySelector("[data-bid-form-target='lineItemQty']")
      const totalCell = row.querySelector("[data-bid-form-target='lineItemTotal']")
      if (!priceInput || !totalCell) return

      const price = this._parseCurrencyToNumber(priceInput.value || "0")
      const qty = parseFloat(qtyInput?.value || "0") || 0
      const total = price * qty
      totalCell.textContent = total > 0 ? this._formatNumber(total) : ""
    })

    this._updateGrandTotal()
    this._updateDollarAmount()
  }

  _updateGrandTotal() {
    if (!this.hasGrandTotalTarget) return

    let grandTotal = 0
    this.lineItemTotalTargets.forEach(cell => {
      const val = this._parseCurrencyToNumber(cell.textContent || "0")
      if (!isNaN(val)) grandTotal += val
    })

    this.grandTotalTarget.textContent = grandTotal > 0 ? this._formatNumber(grandTotal) : "—"
  }

  _updateDollarAmount() {
    if (!this.hasDollarAmountTarget || !this.bidLevelingValue) return

    let grandTotal = 0
    this.lineItemTotalTargets.forEach(cell => {
      const val = this._parseCurrencyToNumber(cell.textContent || "0")
      if (!isNaN(val)) grandTotal += val
    })

    this.dollarAmountTarget.value = grandTotal > 0 ? this._formatWholeNumber(grandTotal) : ""
  }

  // ── Amount validation (inline, on blur) ─────────────────────────

  validateAmounts() {
    const textAmount = this.hasTextAmountTarget ? this.textAmountTarget.value.trim() : ""
    const dollarAmount = this.hasDollarAmountTarget ? this.dollarAmountTarget.value.trim() : ""

    // Only validate when both fields have values
    if (!textAmount || !dollarAmount || !this.validateAmountUrlValue) return

    // Don't re-validate if inputs haven't changed
    const key = `${textAmount}|${dollarAmount}`
    if (this._lastValidatedAmounts === key) return
    this._lastValidatedAmounts = key

    // Hide previous warning while validating
    this._hideAmountWarning()

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const formData = new FormData()
    formData.append("text_amount", textAmount)
    formData.append("numerical_amount", dollarAmount)

    fetch(this.validateAmountUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData
    })
      .then(resp => resp.json())
      .then(data => {
        if (data.valid) {
          this._amountMismatch = false
          this._hideAmountWarning()
        } else {
          this._amountMismatch = true
          this._showAmountWarning(data.msg)
        }
      })
      .catch(() => {
        this._amountMismatch = false
      })
  }

  _showAmountWarning(msg) {
    if (this.hasAmountWarningMsgTarget) {
      this.amountWarningMsgTarget.textContent = msg
    }
    if (this.hasAmountWarningTarget) {
      this.amountWarningTarget.classList.remove("hidden")
    }
  }

  _hideAmountWarning() {
    if (this.hasAmountWarningTarget) {
      this.amountWarningTarget.classList.add("hidden")
    }
  }

  // ── Deadline monitoring ───────────────────────────────────────────

  _checkDeadline() {
    if (!this.deadlineUtcValue) return
    const now = serverTime.now()
    if (!now) return // Server time not yet available
    const deadline = new Date(this.deadlineUtcValue)
    if (now.getTime() >= deadline.getTime()) {
      this._disableFormForDeadline()
      if (this._deadlineInterval) {
        clearInterval(this._deadlineInterval)
        this._deadlineInterval = null
      }
    }
  }

  _disableFormForDeadline() {
    this._deadlineReached = true
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50", "pointer-events-none")
    }
    if (this.hasDeadlineBannerTarget) {
      this.deadlineBannerTargets.forEach(el => el.classList.remove("hidden"))
      this.deadlineBannerTargets.at(-1).scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  // ── Form validation + submit ────────────────────────────────────

  validateAndSubmit(event) {
    // Block if deadline passed
    if (this._deadlineReached) {
      event.preventDefault()
      return
    }

    this._clearAllErrors()
    const errors = []

    // 1. Amount mismatch (AI validation)
    if (this._amountMismatch) {
      event.preventDefault()
      if (this.hasAmountWarningTarget) {
        this.amountWarningTarget.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      return
    }

    // 2. Required contractor fields
    if (this.hasRequiredFieldTarget) {
      let firstRequiredField = null
      this.requiredFieldTargets.forEach(field => {
        const val = field.value?.trim()
        if (!val) {
          this._addErrorBorder(field)
          if (!firstRequiredField) firstRequiredField = field
        }
      })
      if (firstRequiredField) errors.push(firstRequiredField)
    }

    // 3. Amount in words
    if (this.hasTextAmountTarget) {
      const textVal = this.textAmountTarget.value.trim()
      if (!textVal) {
        this._showFieldError(this.textAmountTarget, this.textAmountErrorTarget, this._t("required", "— Required"))
        errors.push(this.textAmountErrorTarget)
      }
    }

    // 4. Bid amount > 0
    if (this.hasDollarAmountTarget) {
      const amount = this._parseCurrencyToNumber(this.dollarAmountTarget.value || "0")
      if (!(amount > 0)) {
        this._showFieldError(this.dollarAmountTarget, this.bidAmountErrorTarget, this._t("required", "— Required"))
        errors.push(this.bidAmountErrorTarget)
      }
    }

    // 5. Bid alternate list — all must be filled (yes_list mode only)
    if (this.hasAlternateListInputTarget) {
      const allFilled = this.alternateListInputTargets.every(input => {
        const val = input.value.trim()
        if (!val) return false
        const num = this._parseCurrencyToNumber(val)
        return !isNaN(num)
      })
      if (!allFilled) {
        this._showFieldError(null, this.alternateListErrorTarget, this._t("alternates_required", "All bid alternate fields are required. If there is no amount, enter 0."))
        this.alternateListInputTargets.forEach(input => {
          const val = input.value.trim()
          if (!val || isNaN(this._parseCurrencyToNumber(val))) this._addErrorBorder(input)
        })
        errors.push(this.alternateListErrorTarget)
      }
    }

    // 6. Addenda — all must be checked
    if (this.hasAddendaCheckboxTarget) {
      const allChecked = this.addendaCheckboxTargets.every(cb => cb.checked)
      if (!allChecked) {
        this._showFieldError(null, this.addendaErrorTarget, this._t("addenda_required", "All addenda must be acknowledged."))
        errors.push(this.addendaErrorTarget)
      }
    }

    // 7. Bid bond doc
    if (this.hasBbDropzoneTarget && this._bbIds.size === 0) {
      this._showFieldError(null, this.bidBondErrorTarget, this._t("upload_required", "— Upload required"))
      errors.push(this.bidBondErrorTarget)
    }

    // 8. Unit pricing doc
    if (this.hasUpDropzoneTarget && this._upIds.size === 0) {
      this._showFieldError(null, this.unitPricingDocErrorTarget, this._t("upload_required", "— Upload required"))
      errors.push(this.unitPricingDocErrorTarget)
    }

    // 9. Other required docs — check each file list has at least one file
    if (this.hasOtherDocErrorTarget) {
      this.otherDocErrorTargets.forEach(errorEl => {
        const docType = errorEl.dataset.docType
        const fileList = this.otherFileListTargets.find(fl => fl.dataset.docType === docType)
        const hasFiles = fileList && fileList.querySelector("[data-attachment-id]")
        if (!hasFiles) {
          this._showFieldError(null, errorEl, this._t("upload_required", "— Upload required"))
          errors.push(errorEl)
        }
      })
    }

    // 10. Unit pricing list values — all must be filled
    if (this.hasUnitPricingListInputTarget) {
      const allFilled = this.unitPricingListInputTargets.every(input => {
        const val = input.value.trim()
        if (!val) return false
        const num = this._parseCurrencyToNumber(val)
        return !isNaN(num) && num >= 0
      })
      if (!allFilled) {
        this._showFieldError(null, this.unitPricingListErrorTarget, this._t("unit_pricing_required", "All unit pricing fields must be filled in. If there is no amount, enter 0."))
        errors.push(this.unitPricingListErrorTarget)
        this.unitPricingListInputTargets.forEach(input => {
          const val = input.value.trim()
          const num = this._parseCurrencyToNumber(val)
          if (!val || isNaN(num) || num < 0) this._addErrorBorder(input)
        })
      }
    }

    // 11. Electronic signature — name must match
    if (this.hasEsignInputTarget) {
      const typed = this._normalizeSignature(this.esignInputTarget.value)
      const expected = this._normalizeSignature(this.signerNameValue)
      if (!typed || typed !== expected) {
        const msg = !typed ? this._t("type_full_name", "Please type your full name to sign.") : this._t("signature_must_match", "Signature must match your full name.")
        this._showFieldError(this.esignInputTarget, this.esignErrorTarget, msg)
        errors.push(this.esignErrorTarget)
      }
    }

    // If errors, prevent submit and scroll to first
    if (errors.length > 0) {
      event.preventDefault()
      errors[0].scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    // No errors — collect line item bids JSON before submit
    if (this.bidLevelingValue && this.hasLineItemBidsJsonTarget) {
      const bids = []
      this.lineItemRowTargets.forEach(row => {
        const lineItemId = row.dataset.lineItemId
        const priceInput = row.querySelector("[data-bid-form-target='lineItemPrice']")
        const qtyInput = row.querySelector("[data-bid-form-target='lineItemQty']")
        const price = this._parseCurrencyToNumber(priceInput?.value || "0")
        const qty = parseFloat(qtyInput?.value || "0") || 0

        if (price > 0) {
          bids.push({
            line_item_id: parseInt(lineItemId),
            amount: price,
            quantity: qty,
            comment: ""
          })
        }
      })
      this.lineItemBidsJsonTarget.value = JSON.stringify(bids)
    }

    // Disable submit button to prevent double-click
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50")
    }
  }

  // ── Error border helpers ──────────────────────────────────────────

  _addErrorBorder(el) {
    el.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _removeErrorBorder(el) {
    el.classList.remove("border-red-400", "ring-1", "ring-red-400")
  }

  // ── Inline error helpers ──────────────────────────────────────────

  _clearAllErrors() {
    const errorTargets = [
      "textAmountError", "bidAmountError", "alternateListError", "addendaError", "bidBondError",
      "unitPricingDocError", "unitPricingListError", "esignError"
    ]
    errorTargets.forEach(name => {
      const hasMethod = `has${name.charAt(0).toUpperCase() + name.slice(1)}Target`
      if (this[hasMethod]) {
        this[`${name}Target`].classList.add("hidden")
      }
    })

    if (this.hasOtherDocErrorTarget) {
      this.otherDocErrorTargets.forEach(el => el.classList.add("hidden"))
    }

    // Remove red borders from specific fields
    if (this.hasTextAmountTarget) this._removeErrorBorder(this.textAmountTarget)
    if (this.hasDollarAmountTarget) this._removeErrorBorder(this.dollarAmountTarget)
    if (this.hasEsignInputTarget) this._removeErrorBorder(this.esignInputTarget)
    if (this.hasAlternateListInputTarget) {
      this.alternateListInputTargets.forEach(input => this._removeErrorBorder(input))
    }
    if (this.hasUnitPricingListInputTarget) {
      this.unitPricingListInputTargets.forEach(input => this._removeErrorBorder(input))
    }

    // Clear required field errors
    if (this.hasRequiredFieldTarget) {
      this.requiredFieldTargets.forEach(field => this._removeErrorBorder(field))
    }
  }

  _showFieldError(field, errorEl, message) {
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove("hidden")
    }
    if (field) {
      this._addErrorBorder(field)
    }
  }

  clearRequiredFieldError(event) {
    const field = event.currentTarget
    if (field.value?.trim()) {
      this._removeErrorBorder(field)
    }
  }

  clearTextAmountError() {
    if (this.hasTextAmountErrorTarget) this.textAmountErrorTarget.classList.add("hidden")
    if (this.hasTextAmountTarget) this._removeErrorBorder(this.textAmountTarget)
  }

  clearBidAmountError() {
    if (this.hasBidAmountErrorTarget) this.bidAmountErrorTarget.classList.add("hidden")
    if (this.hasDollarAmountTarget) this._removeErrorBorder(this.dollarAmountTarget)
  }

  clearAlternateListError(event) {
    if (!this.hasAlternateListInputTarget) return

    const input = event?.currentTarget
    if (input && input.value.trim()) {
      this._removeErrorBorder(input)
    }

    if (this.hasAlternateListErrorTarget) {
      const allFilled = this.alternateListInputTargets.every(i => {
        const val = i.value.trim()
        return val && !isNaN(this._parseCurrencyToNumber(val))
      })
      if (allFilled) {
        this.alternateListErrorTarget.classList.add("hidden")
        this.alternateListInputTargets.forEach(i => this._removeErrorBorder(i))
      }
    }
  }

  clearAddendaError() {
    if (!this.hasAddendaErrorTarget) return
    const allChecked = this.addendaCheckboxTargets.every(cb => cb.checked)
    if (allChecked) this.addendaErrorTarget.classList.add("hidden")
  }

  clearEsignError() {
    if (!this.hasEsignErrorTarget) return
    const typed = this._normalizeSignature(this.esignInputTarget.value)
    const expected = this._normalizeSignature(this.signerNameValue)
    if (typed && typed === expected) {
      this.esignErrorTarget.classList.add("hidden")
      this._removeErrorBorder(this.esignInputTarget)
    }
  }

  // The typed signature still has to be the signer's name — but accents are
  // not the point of the check (Phelipe 2026-08-06). A Latin-American signer
  // typing "Mario Garcia" for a profile reading "Mario García" was rejected,
  // and so was anyone whose keyboard drops the accent. Strips diacritics
  // (García → garcia, Conceição → conceicao) and collapses stray spaces;
  // adding or missing a NAME still fails, which is the part that matters.
  _normalizeSignature(value) {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // the combining marks NFD split off
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
  }

  clearUnitPricingListError(event) {
    if (!this.hasUnitPricingListInputTarget) return

    // Clear border on the individual input being edited if it now has a value
    const input = event?.currentTarget
    if (input && input.value.trim()) {
      this._removeErrorBorder(input)
    }

    // Hide the error message if all fields are filled
    if (this.hasUnitPricingListErrorTarget) {
      const allFilled = this.unitPricingListInputTargets.every(i => i.value.trim().length > 0)
      if (allFilled) {
        this.unitPricingListErrorTarget.classList.add("hidden")
        this.unitPricingListInputTargets.forEach(i => this._removeErrorBorder(i))
      }
    }
  }

  _clearDocError(docType) {
    if (docType === "BidBond" && this.hasBidBondErrorTarget) {
      this.bidBondErrorTarget.classList.add("hidden")
    } else if (docType === "UnitPricing" && this.hasUnitPricingDocErrorTarget) {
      this.unitPricingDocErrorTarget.classList.add("hidden")
    } else if (this.hasOtherDocErrorTarget) {
      const errorEl = this.otherDocErrorTargets.find(el => el.dataset.docType === docType)
      if (errorEl) errorEl.classList.add("hidden")
    }
  }

  async countryChanged() {
    if (!this.hasStateSelectTarget) return
    const select = this.stateSelectTarget
    select.innerHTML = '<option value="">Select...</option>'

    const code = this.countrySelectTarget.value
    if (!code) return

    try {
      const resp = await fetch(`/locations/subregions?country_code=${code}`, {
        headers: { Accept: "application/json" }
      })
      const subregions = await resp.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.name
        opt.textContent = sr.name
        select.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  showWithdrawModal() {
    if (this.hasWithdrawModalTarget) {
      this.withdrawModalTarget.classList.remove("hidden")
    }
  }

  hideWithdrawModal() {
    if (this.hasWithdrawModalTarget) {
      this.withdrawModalTarget.classList.add("hidden")
    }
  }

  _t(key, fallback) {
    return (this.i18nValue && this.i18nValue[key]) || fallback
  }
}
